import React, { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Loader2 } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_cpd-compass/artifacts/km6w43n1_Teal%20Cerebro%20Transp.png";

export default function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const { handleOAuthCallback, establishSsoSession } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processCallback = async () => {
      try {
        // Extract session_id from URL hash
        const hash = location.hash;
        const sessionIdMatch = hash.match(/session_id=([^&]+)/);

        if (!sessionIdMatch) {
          console.error('No session_id in URL');
          navigate('/login', { replace: true });
          return;
        }

        const sessionId = decodeURIComponent(sessionIdMatch[1]);

        // Entra SSO (routes/sso.py) puts the finished JWT straight into the
        // fragment and marks it with sso=true. There is nothing to exchange -
        // the older flow POSTed this value to /api/auth/session, an endpoint
        // that no longer exists, which is why SSO logins failed here.
        const user = hash.includes('sso=true')
          ? await establishSsoSession(sessionId)
          : await handleOAuthCallback(sessionId);

        // Navigate to dashboard with user data
        navigate('/dashboard', {
          replace: true,
          state: { user }
        });
      } catch (error) {
        console.error('Auth callback error:', error);
        navigate('/login', {
          replace: true,
          state: { error: 'Authentication failed. Please try again.' }
        });
      }
    };

    processCallback();
  }, [location.hash, handleOAuthCallback, establishSsoSession, navigate]);

  return (
    <div className="min-h-screen bg-navigate-dark flex items-center justify-center">
      <div className="text-center">
        <img 
          src={LOGO_URL} 
          alt="Cerebro" 
          className="h-16 w-16 mx-auto mb-6 animate-pulse"
        />
        <Loader2 className="h-8 w-8 animate-spin text-navigate-gold mx-auto mb-4" />
        <p className="text-zinc-400">Completing sign in...</p>
      </div>
    </div>
  );
}
